import React, { useState, useEffect, useCallback, useRef } from "react";

/**
 * PM Work Order Review Component
 *
 * Speed-optimized UI for reviewing AI-extracted project data.
 * Designed for keyboard-first navigation and minimal clicks.
 *
 * Key Features:
 * - Tab navigation between fields
 * - Space to toggle checkboxes
 * - Cmd+Enter to approve all
 * - Visual confidence indicators
 * - Bulk actions for common operations
 * - Complete work order fields based on 3D Color work order template
 */

// Work center toggle button with keyboard support
function WorkCenterToggle({ label, checked, onChange, shortcut, tabIndex }) {
  return (
    <button
      type="button"
      tabIndex={tabIndex}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className={`
        w-12 h-8 rounded font-medium text-xs transition-all
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
        ${
          checked
            ? "bg-blue-600 text-gray-900 shadow-sm"
            : "bg-slate-100 text-gray-500 hover:bg-gray-200"
        }
      `}
      title={`${label} (${shortcut})`}
    >
      {checked ? "Yes" : "No"}
    </button>
  );
}

// Editable field with inline editing
function EditableField({ label, value, onChange, confidence, tabIndex, type = "text", options = null, width = "flex-1" }) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === "text") {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  const confidenceColor =
    confidence >= 0.9
      ? "text-green-600"
      : confidence >= 0.7
        ? "text-yellow-600"
        : "text-red-600";

  // Select dropdown
  if (options) {
    return (
      <div className={`flex items-center gap-2 ${width}`}>
        {label && <span className="text-gray-500 text-sm w-20 shrink-0">{label}</span>}
        <select
          ref={inputRef}
          value={localValue || ""}
          onChange={(e) => {
            setLocalValue(e.target.value);
            onChange(e.target.value);
          }}
          tabIndex={tabIndex}
          className={`${width === "flex-1" ? "flex-1" : width} px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
        >
          <option value="">Select...</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${width}`}>
      {label && <span className="text-gray-500 text-sm w-20 shrink-0">{label}</span>}
      {isEditing ? (
        <input
          ref={inputRef}
          type={type}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          tabIndex={tabIndex}
          className={`${width === "flex-1" ? "flex-1" : width} px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
      ) : (
        <button
          type="button"
          tabIndex={tabIndex}
          onClick={() => setIsEditing(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsEditing(true);
            }
          }}
          className={`${width === "flex-1" ? "flex-1" : width} text-left px-2 py-1 rounded hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
        >
          <span className="font-medium">{value || "—"}</span>
          {confidence !== undefined && (
            <span className={`ml-2 text-xs ${confidenceColor}`}>
              {confidence >= 0.9 ? "✓" : confidence >= 0.7 ? "?" : "!"}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

// Expanded SKU Detail Panel
function SKUDetailPanel({ sku, index, onChange, onClose, options }) {
  const handleChange = (field, value) => {
    onChange(index, { ...sku, [field]: value });
  };

  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-2 rounded-r-lg">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-700">
          SKU {index + 1} Details: {sku.sku_name || "Untitled"}
        </h4>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-600 text-lg"
        >
          ×
        </button>
      </div>

      {/* POA Details */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">POA Type</label>
          <select
            value={sku.poa_type || ""}
            onChange={(e) => handleChange("poa_type", e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.poaTypes || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">File Location</label>
          <select
            value={sku.file_location || ""}
            onChange={(e) => handleChange("file_location", e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.fileLocations || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Color Target</label>
          <select
            value={sku.color_target || ""}
            onChange={(e) => handleChange("color_target", e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.colorTargets || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Substrate</label>
          <select
            value={sku.substrate || ""}
            onChange={(e) => handleChange("substrate", e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.substrates || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Varnish</label>
          <select
            value={sku.varnish || ""}
            onChange={(e) => handleChange("varnish", e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.varnishOptions || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Special Effects</label>
          <select
            value={sku.special_effects || ""}
            onChange={(e) => handleChange("special_effects", e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.specialEffects || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Work Center Flags */}
      <div className="flex items-center gap-6 mb-4 p-3 bg-white rounded border border-gray-200">
        <span className="text-sm text-gray-500">Work Centers:</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sku.needs_production_art || false}
            onChange={(e) => handleChange("needs_production_art", e.target.checked)}
            className="w-4 h-4 rounded text-blue-600"
          />
          <span className="text-sm">Prod Art</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sku.needs_color_development || false}
            onChange={(e) => handleChange("needs_color_development", e.target.checked)}
            className="w-4 h-4 rounded text-blue-600"
          />
          <span className="text-sm">Color Dev</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sku.needs_opti || false}
            onChange={(e) => handleChange("needs_opti", e.target.checked)}
            className="w-4 h-4 rounded text-purple-600"
          />
          <span className="text-sm">Opti/UV</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sku.needs_outsource || false}
            onChange={(e) => handleChange("needs_outsource", e.target.checked)}
            className="w-4 h-4 rounded text-orange-600"
          />
          <span className="text-sm">Outsource</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sku.needs_3d_model || sku.needs_model || false}
            onChange={(e) => handleChange("needs_3d_model", e.target.checked)}
            className="w-4 h-4 rounded text-green-600"
          />
          <span className="text-sm">3D/Model</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sku.needs_paint || false}
            onChange={(e) => handleChange("needs_paint", e.target.checked)}
            className="w-4 h-4 rounded text-red-600"
          />
          <span className="text-sm">Paint</span>
        </label>
      </div>

      {/* Notes */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Production Art Notes</label>
          <textarea
            value={sku.production_art_notes || ""}
            onChange={(e) => handleChange("production_art_notes", e.target.value)}
            placeholder="Instructions for production art..."
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 resize-none"
            rows={2}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Finishing & Assembly Notes</label>
          <textarea
            value={sku.finishing_notes || ""}
            onChange={(e) => handleChange("finishing_notes", e.target.value)}
            placeholder="Finishing instructions..."
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 resize-none"
            rows={2}
          />
        </div>
      </div>

      {/* Proof Plan */}
      <div className="mt-4 p-3 bg-white rounded border border-gray-200">
        <span className="text-sm text-gray-500 mr-4">Proof Plan:</span>
        <label className="inline-flex items-center gap-2 mr-6 cursor-pointer">
          <input
            type="radio"
            name={`proof_plan_${index}`}
            checked={sku.client_approval_required === true}
            onChange={() => {
              handleChange("client_approval_required", true);
              handleChange("internal_review_only", false);
            }}
            className="w-4 h-4 text-blue-600"
          />
          <span className="text-sm">Client Approval Required</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`proof_plan_${index}`}
            checked={sku.internal_review_only === true}
            onChange={() => {
              handleChange("internal_review_only", true);
              handleChange("client_approval_required", false);
            }}
            className="w-4 h-4 text-blue-600"
          />
          <span className="text-sm">Internal Review Only</span>
        </label>
      </div>

      {/* Structure Plan */}
      <div className="mt-3">
        <label className="block text-xs text-gray-500 mb-1">Structure Source</label>
        <select
          value={sku.structure_source || ""}
          onChange={(e) => handleChange("structure_source", e.target.value)}
          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select...</option>
          {(options?.structureSources || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// SKU Row with all work center toggles
function SKURow({ sku, index, onChange, isSelected, onSelect, onExpand, isExpanded, tabIndexBase }) {
  const handleWorkCenterChange = (field, value) => {
    onChange(index, { ...sku, [field]: value });
  };

  // Click anywhere on row to expand/collapse details
  const handleRowClick = (e) => {
    // Don't expand if clicking on form controls
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    onExpand(index);
  };

  return (
    <tr
      className={`
        border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer
        ${isSelected ? "bg-blue-100" : ""}
        ${isExpanded ? "bg-blue-50 ring-2 ring-blue-400" : ""}
      `}
      onClick={handleRowClick}
    >
      <td className="py-2 px-3 text-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(index)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
        />
      </td>
      <td className="py-2 px-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={sku.sku_name || sku.description || ""}
            onChange={(e) => handleWorkCenterChange("sku_name", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            tabIndex={tabIndexBase}
            className="flex-1 px-2 py-1 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            placeholder="SKU Description"
          />
          <span className={`text-xs ${isExpanded ? "text-blue-600" : "text-gray-500"}`}>
            {isExpanded ? "▼" : "▶"}
          </span>
        </div>
      </td>
      <td className="py-2 px-3 text-center">
        <input
          type="number"
          value={sku.quantity || 1}
          onChange={(e) =>
            handleWorkCenterChange("quantity", parseInt(e.target.value) || 1)
          }
          onClick={(e) => e.stopPropagation()}
          tabIndex={tabIndexBase + 1}
          className="w-14 px-2 py-1 text-center border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
        />
      </td>
      <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        <WorkCenterToggle
          label="3D/Model"
          checked={sku.needs_model || sku.needs_3d_model}
          onChange={(v) => {
            handleWorkCenterChange("needs_model", v);
            handleWorkCenterChange("needs_3d_model", v);
          }}
          shortcut="M"
          tabIndex={tabIndexBase + 2}
        />
      </td>
      <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        <WorkCenterToggle
          label="Paint"
          checked={sku.needs_paint}
          onChange={(v) => handleWorkCenterChange("needs_paint", v)}
          shortcut="P"
          tabIndex={tabIndexBase + 3}
        />
      </td>
      <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        <WorkCenterToggle
          label="Graphics"
          checked={sku.needs_graphics || sku.needs_production_art}
          onChange={(v) => {
            handleWorkCenterChange("needs_graphics", v);
            handleWorkCenterChange("needs_production_art", v);
          }}
          shortcut="G"
          tabIndex={tabIndexBase + 4}
        />
      </td>
      <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        <WorkCenterToggle
          label="Opti"
          checked={sku.needs_opti}
          onChange={(v) => handleWorkCenterChange("needs_opti", v)}
          shortcut="O"
          tabIndex={tabIndexBase + 5}
        />
      </td>
      <td className="py-2 px-3 text-center">
        {sku.jobBossMatch ? (
          <span className="text-green-600 text-sm" title="Matched to JobBoss quote">✓</span>
        ) : sku.source === "merged" ? (
          <span className="text-blue-600 text-sm" title="AI + JobBoss merged">⚡</span>
        ) : (
          <span className="text-gray-500 text-sm">—</span>
        )}
      </td>
    </tr>
  );
}

// SKU Card for card view - shows all details at once
function SKUCard({ sku, index, onChange, isSelected, onSelect, options }) {
  const handleChange = (field, value) => {
    onChange(index, { ...sku, [field]: value });
  };

  return (
    <div
      className={`
        bg-white border-2 rounded-lg p-4 transition-all
        ${isSelected ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200 hover:border-gray-300"}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(index)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-500">SKU #{index + 1}</span>
          {sku.jobBossMatch && (
            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">JobBoss ✓</span>
          )}
        </div>
        <span className="text-xs text-gray-500">{sku.source || "email"}</span>
      </div>

      {/* Description */}
      <input
        type="text"
        value={sku.sku_name || sku.description || ""}
        onChange={(e) => handleChange("sku_name", e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded mb-3 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="SKU Description"
      />

      {/* Qty & Part Number */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-gray-500">Quantity</label>
          <input
            type="number"
            value={sku.quantity || 1}
            onChange={(e) => handleChange("quantity", parseInt(e.target.value) || 1)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Part #</label>
          <input
            type="text"
            value={sku.part_number || ""}
            onChange={(e) => handleChange("part_number", e.target.value)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="—"
          />
        </div>
      </div>

      {/* Work Centers - compact toggles */}
      <div className="mb-3">
        <label className="text-xs text-gray-500 mb-1 block">Work Centers</label>
        <div className="flex flex-wrap gap-2">
          {[
            { key: "needs_model", label: "3D", alt: "needs_3d_model" },
            { key: "needs_paint", label: "Paint" },
            { key: "needs_graphics", label: "Graph", alt: "needs_production_art" },
            { key: "needs_opti", label: "Opti" },
            { key: "needs_color_development", label: "Color" },
            { key: "needs_outsource", label: "Outsrc" },
          ].map(({ key, label, alt }) => (
            <button
              key={key}
              onClick={() => {
                handleChange(key, !sku[key]);
                if (alt) handleChange(alt, !sku[key]);
              }}
              className={`
                px-2 py-1 text-xs rounded transition-colors
                ${sku[key] || (alt && sku[alt])
                  ? "bg-blue-600 text-gray-900"
                  : "bg-slate-100 text-gray-500 hover:bg-gray-200"
                }
              `}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* POA Details */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <label className="text-xs text-gray-500">POA Type</label>
          <select
            value={sku.poa_type || ""}
            onChange={(e) => handleChange("poa_type", e.target.value)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.poaTypes || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Substrate</label>
          <select
            value={sku.substrate || ""}
            onChange={(e) => handleChange("substrate", e.target.value)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.substrates || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Varnish</label>
          <select
            value={sku.varnish || ""}
            onChange={(e) => handleChange("varnish", e.target.value)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.varnishOptions || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Special Effects</label>
          <select
            value={sku.special_effects || ""}
            onChange={(e) => handleChange("special_effects", e.target.value)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {(options?.specialEffects || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Client Approval Toggle */}
      <div className="mt-3 flex items-center gap-2">
        <input
          type="checkbox"
          id={`approval-${index}`}
          checked={sku.client_approval_required ?? true}
          onChange={(e) => handleChange("client_approval_required", e.target.checked)}
          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
        />
        <label htmlFor={`approval-${index}`} className="text-xs text-gray-600">
          Client approval required
        </label>
      </div>
    </div>
  );
}

// Bulk action bar
function BulkActionBar({ selectedCount, onBulkAction }) {
  if (selectedCount === 0) return null;

  return (
    <div className="bg-blue-600 text-gray-900 px-4 py-2 rounded-lg flex items-center gap-4 text-sm">
      <span className="font-medium">{selectedCount} selected</span>
      <div className="flex gap-2">
        <button
          onClick={() => onBulkAction("needs_model", true)}
          className="px-2 py-1 bg-blue-500 hover:bg-blue-400 rounded"
        >
          +3D All
        </button>
        <button
          onClick={() => onBulkAction("needs_paint", true)}
          className="px-2 py-1 bg-blue-500 hover:bg-blue-400 rounded"
        >
          +Paint All
        </button>
        <button
          onClick={() => onBulkAction("needs_graphics", true)}
          className="px-2 py-1 bg-blue-500 hover:bg-blue-400 rounded"
        >
          +Graphics All
        </button>
        <button
          onClick={() => onBulkAction("needs_opti", true)}
          className="px-2 py-1 bg-blue-500 hover:bg-blue-400 rounded"
        >
          +Opti All
        </button>
      </div>
    </div>
  );
}

// Main PM Work Order Review Component
export default function PMWorkOrderReview({
  intake,
  extractedData,
  onApprove,
  onSave,
  onSkip,
  onRequestClarification,
  isDemo = false,
}) {
  const [projectData, setProjectData] = useState({
    customer_name: "",
    project_name: "",
    quote_number: "",
    po_number: "",
    ship_date: "",
    is_rush: false,
    contact_name: "",
    project_manager: "",
    sales_rep: "",
    skus: [],
  });
  const [selectedSKUs, setSelectedSKUs] = useState(new Set());
  const [expandedSKU, setExpandedSKU] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [formOptions, setFormOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [merging, setMerging] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // "table" or "cards"

  // Fetch form options
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const token = sessionStorage.getItem("msal.backendToken");
        const res = await fetch("/api/pm-center/work-orders/options", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setFormOptions(data);
        }
      } catch (err) {
        console.error("Failed to load form options:", err);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, []);

  // Track the intake ID to know when we switch to a different work order
  const [currentIntakeId, setCurrentIntakeId] = useState(null);

  // Initialize from extracted data only when intake changes (not when SKUs update from merge)
  useEffect(() => {
    if (extractedData && intake?.id !== currentIntakeId) {
      // New intake - initialize all data
      setProjectData({
        customer_name: extractedData.customer_name || "",
        project_name: extractedData.project_name || "",
        quote_number: extractedData.quote_number || "",
        po_number: extractedData.po_number || "",
        ship_date: extractedData.ship_date || "",
        is_rush: extractedData.is_rush || false,
        contact_name: extractedData.contact_name || "",
        project_manager: extractedData.project_manager || "",
        sales_rep: extractedData.sales_rep || "",
        skus: extractedData.skus || [],
      });
      setExpandedSKU(null);
      setCurrentIntakeId(intake?.id);
    } else if (extractedData && intake?.id === currentIntakeId) {
      // Same intake but data updated (e.g., JobBoss loaded) - only update SKUs if we have better data
      // This preserves user edits while allowing JobBoss data to load
      if (extractedData.jobBossLinked && extractedData.skus?.length > 0) {
        setProjectData((prev) => ({
          ...prev,
          skus: extractedData.skus,
        }));
      }
    }
  }, [extractedData, intake?.id, currentIntakeId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+Enter or Ctrl+Enter to approve
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleApprove();
      }
      // Escape to skip
      if (e.key === "Escape") {
        if (expandedSKU !== null) {
          setExpandedSKU(null);
        } else {
          onSkip?.();
        }
      }
      // ? to toggle shortcuts help
      if (e.key === "?" && !e.target.tagName.match(/INPUT|TEXTAREA|SELECT/)) {
        setShowShortcuts((s) => !s);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [projectData, onSkip, expandedSKU]);

  const handleFieldChange = (field, value) => {
    setProjectData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSKUChange = (index, updatedSKU) => {
    setProjectData((prev) => ({
      ...prev,
      skus: prev.skus.map((s, i) => (i === index ? updatedSKU : s)),
    }));
  };

  const handleSKUSelect = (index) => {
    setSelectedSKUs((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSKUExpand = (index) => {
    setExpandedSKU(expandedSKU === index ? null : index);
  };

  const handleBulkAction = (field, value) => {
    setProjectData((prev) => ({
      ...prev,
      skus: prev.skus.map((s, i) =>
        selectedSKUs.has(i) ? { ...s, [field]: value } : s
      ),
    }));
  };

  const handleSave = () => {
    onSave?.(projectData);
  };

  const handlePushToProduction = () => {
    onApprove?.(projectData);
  };

  const handleAIMerge = async () => {
    if (!projectData.quote_number) {
      alert("Please enter a quote number first");
      return;
    }

    setMerging(true);
    try {
      const token = sessionStorage.getItem("msal.backendToken");

      // For demo mode or when intake doesn't have a real DB ID, use direct merge endpoint
      const isRealId = intake?.id && !intake.id.startsWith("demo-");

      if (isRealId) {
        // Live mode - use the merge endpoint that saves to DB
        const res = await fetch(`/api/pm-center/work-orders/merge/${intake.id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ quoteNumber: projectData.quote_number }),
        });

        if (res.ok) {
          const data = await res.json();
          if (data.merge?.skus) {
            setProjectData((prev) => ({
              ...prev,
              skus: data.merge.skus,
            }));
          }
          console.log("AI Merge complete:", data.merge);
        } else {
          throw new Error("Merge failed");
        }
      } else {
        // Demo/direct mode - just fetch JobBoss line items directly
        const res = await fetch(`/api/pm-center/quotes/${projectData.quote_number}/line-items`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.lineItems?.length > 0) {
            // Merge JobBoss line items with existing extracted SKUs
            const mergedSkus = data.lineItems.map((lineItem, idx) => {
              // Try to find matching AI extraction
              const aiMatch = (projectData.skus || []).find(
                (sku) =>
                  sku.sku_name?.toLowerCase().includes(lineItem.description?.toLowerCase()?.substring(0, 15)) ||
                  lineItem.description?.toLowerCase().includes(sku.sku_name?.toLowerCase()?.substring(0, 15))
              );

              return {
                sku_number: idx + 1,
                sku_name: lineItem.description || lineItem.partNumber,
                part_number: lineItem.partNumber,
                quantity: lineItem.quantity1 || lineItem.quantityOrdered || 1,
                price: lineItem.price1 || lineItem.unitPrice,
                // Merge work center flags
                needs_model: aiMatch?.needs_model || false,
                needs_3d_model: aiMatch?.needs_model || false,
                needs_paint: aiMatch?.needs_paint || false,
                needs_graphics: aiMatch?.needs_graphics ?? true,
                needs_production_art: true,
                needs_opti: aiMatch?.needs_opti || false,
                needs_color_development: false,
                needs_outsource: false,
                // Default POA values
                poa_type: "Carton - Production Die",
                file_location: "Supplied Folder",
                color_target: "Match PMS in File",
                substrate: "8300 Gloss Transfer to 18pt C1S",
                varnish: "N/A",
                special_effects: "N/A",
                client_approval_required: true,
                // Source tracking
                jobBossMatch: true,
                source: aiMatch ? "merged" : "jobboss",
                jobboss_line_number: lineItem.lineNumber,
              };
            });

            setProjectData((prev) => ({
              ...prev,
              skus: mergedSkus,
            }));
            console.log(`Loaded ${mergedSkus.length} SKUs from JobBoss quote ${projectData.quote_number}`);
          } else {
            alert("No line items found in JobBoss for this quote number");
          }
        } else {
          throw new Error("Failed to fetch JobBoss data");
        }
      }
    } catch (err) {
      console.error("AI Merge error:", err);
      alert("Failed to merge data: " + err.message);
    } finally {
      setMerging(false);
    }
  };

  const handleAddSKU = () => {
    setProjectData((prev) => ({
      ...prev,
      skus: [
        ...prev.skus,
        {
          sku_name: "",
          quantity: 1,
          needs_model: false,
          needs_3d_model: false,
          needs_paint: false,
          needs_graphics: false,
          needs_production_art: true,
          needs_opti: false,
          needs_color_development: false,
          needs_outsource: false,
          poa_type: "Carton - Production Die",
          file_location: "Supplied Folder",
          color_target: "Match PMS in File",
          substrate: "8300 Gloss Transfer to 18pt C1S",
          varnish: "N/A",
          special_effects: "N/A",
          client_approval_required: true,
        },
      ],
    }));
  };

  // Calculate confidence
  const confidence = extractedData?.confidence || 0;
  const confidencePercent = Math.round(confidence * 100);
  const canQuickApprove = confidence >= 0.9 && projectData.skus.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-gray-900 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">
              {intake?.email_subject || "Work Order Review"}
            </h2>
            <p className="text-blue-200 text-sm mt-1">
              From: {intake?.email_from || "—"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onSkip}
              className="px-3 py-2 bg-blue-500 hover:bg-blue-400 rounded-lg transition-colors text-sm"
            >
              Skip
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-white text-blue-700 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium"
            >
              Save Work Order
            </button>
            <button
              onClick={handlePushToProduction}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm ${
                canQuickApprove
                  ? "bg-green-500 hover:bg-green-400 text-gray-900"
                  : "bg-orange-500 hover:bg-orange-400 text-gray-900"
              }`}
            >
              {canQuickApprove && <span>⚡</span>}
              Complete & Push
              <span className="text-xs opacity-75">(⌘+↵)</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confidence Banner */}
      <div
        className={`px-6 py-2 text-sm flex items-center justify-between ${
          confidence >= 0.9
            ? "bg-green-50 text-green-700"
            : confidence >= 0.7
              ? "bg-yellow-50 text-yellow-700"
              : "bg-red-50 text-red-700"
        }`}
      >
        <span>
          AI Confidence: <strong>{confidencePercent}%</strong>
          {confidence >= 0.9 && " — Ready for quick approval"}
          {confidence < 0.7 && " — Needs review"}
        </span>
        <div className="flex items-center gap-4">
          <span>
            {projectData.skus.length} SKU{projectData.skus.length !== 1 && "s"} |{" "}
            {projectData.quote_number
              ? `Quote #${projectData.quote_number}`
              : "No quote"}
          </span>
          {projectData.quote_number && (
            <button
              onClick={handleAIMerge}
              disabled={merging}
              className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
            >
              {merging ? "Merging..." : "🤖 AI Merge with JobBoss"}
            </button>
          )}
        </div>
      </div>

      {/* Project Header Fields */}
      <div className="px-6 py-4 border-b border-gray-200 bg-slate-200">
        <div className="grid grid-cols-3 gap-4">
          <EditableField
            label="Customer"
            value={projectData.customer_name}
            onChange={(v) => handleFieldChange("customer_name", v)}
            confidence={confidence}
            tabIndex={1}
          />
          <EditableField
            label="Quote #"
            value={projectData.quote_number}
            onChange={(v) => handleFieldChange("quote_number", v)}
            confidence={confidence}
            tabIndex={2}
          />
          <EditableField
            label="PO #"
            value={projectData.po_number}
            onChange={(v) => handleFieldChange("po_number", v)}
            tabIndex={3}
          />
          <EditableField
            label="Project"
            value={projectData.project_name}
            onChange={(v) => handleFieldChange("project_name", v)}
            confidence={confidence}
            tabIndex={4}
          />
          <EditableField
            label="Contact"
            value={projectData.contact_name}
            onChange={(v) => handleFieldChange("contact_name", v)}
            tabIndex={5}
          />
          <div className="flex items-center gap-4">
            <EditableField
              label="Ship Date"
              value={projectData.ship_date}
              onChange={(v) => handleFieldChange("ship_date", v)}
              type="date"
              confidence={confidence}
              tabIndex={6}
            />
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={projectData.is_rush}
                onChange={(e) => handleFieldChange("is_rush", e.target.checked)}
                className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                tabIndex={7}
              />
              <span
                className={`text-sm font-medium ${projectData.is_rush ? "text-red-600" : "text-gray-500"}`}
              >
                RUSH
              </span>
            </label>
          </div>
        </div>

        {/* Second row - PM and Sales Rep */}
        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
          <EditableField
            label="PM"
            value={projectData.project_manager}
            onChange={(v) => handleFieldChange("project_manager", v)}
            tabIndex={8}
          />
          <EditableField
            label="Sales Rep"
            value={projectData.sales_rep}
            onChange={(v) => handleFieldChange("sales_rep", v)}
            tabIndex={9}
          />
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedSKUs.size > 0 && (
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <BulkActionBar
            selectedCount={selectedSKUs.size}
            onBulkAction={handleBulkAction}
          />
        </div>
      )}

      {/* SKU Grid */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-700">
            SKUs / Deliverables ({projectData.skus.length})
          </h3>
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  viewMode === "table"
                    ? "bg-white text-gray-800 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Table
              </button>
              <button
                onClick={() => setViewMode("cards")}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  viewMode === "cards"
                    ? "bg-white text-gray-800 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Cards
              </button>
            </div>
            <button
              onClick={handleAddSKU}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <span>+</span> Add SKU
            </button>
          </div>
        </div>

        {/* Card View */}
        {viewMode === "cards" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectData.skus.map((sku, index) => (
              <SKUCard
                key={`card-${index}`}
                sku={sku}
                index={index}
                onChange={handleSKUChange}
                isSelected={selectedSKUs.has(index)}
                onSelect={handleSKUSelect}
                options={formOptions}
              />
            ))}
          </div>
        )}

        {/* Table View */}
        {viewMode === "table" && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 text-left text-sm text-gray-500">
                  <th className="py-2 px-3 w-10">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSKUs(new Set(projectData.skus.map((_, i) => i)));
                        } else {
                          setSelectedSKUs(new Set());
                        }
                      }}
                      checked={selectedSKUs.size === projectData.skus.length && projectData.skus.length > 0}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="py-2 px-3">Description <span className="text-xs text-gray-500 font-normal">(click row to expand)</span></th>
                  <th className="py-2 px-3 text-center w-16">Qty</th>
                  <th className="py-2 px-3 text-center w-14">3D</th>
                  <th className="py-2 px-3 text-center w-14">Paint</th>
                  <th className="py-2 px-3 text-center w-14">Graph</th>
                  <th className="py-2 px-3 text-center w-14">Opti</th>
                  <th className="py-2 px-3 text-center w-16">Source</th>
                </tr>
              </thead>
              <tbody>
                {projectData.skus.map((sku, index) => (
                  <React.Fragment key={`sku-${index}`}>
                    <SKURow
                      sku={sku}
                      index={index}
                      onChange={handleSKUChange}
                      isSelected={selectedSKUs.has(index)}
                      onSelect={handleSKUSelect}
                      onExpand={handleSKUExpand}
                      isExpanded={expandedSKU === index}
                      tabIndexBase={100 + index * 10}
                    />
                    {expandedSKU === index && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <SKUDetailPanel
                            sku={sku}
                            index={index}
                            onChange={handleSKUChange}
                            onClose={() => setExpandedSKU(null)}
                            options={formOptions}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {projectData.skus.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No SKUs detected.{" "}
            <button
              onClick={handleAddSKU}
              className="text-blue-600 hover:underline"
            >
              Add one manually
            </button>
            {projectData.quote_number && (
              <>
                {" or "}
                <button
                  onClick={handleAIMerge}
                  disabled={merging}
                  className="text-purple-600 hover:underline"
                >
                  {merging ? "Loading..." : "load from JobBoss"}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer with shortcuts */}
      <div className="px-6 py-3 bg-slate-200 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center gap-4">
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Tab</kbd>{" "}
            Navigate
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">
              Space
            </kbd>{" "}
            Toggle
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">
              ⌘+↵
            </kbd>{" "}
            Approve
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Esc</kbd>{" "}
            {expandedSKU !== null ? "Close Detail" : "Skip"}
          </span>
        </div>
        <button
          onClick={() => onRequestClarification?.(projectData)}
          className="text-blue-600 hover:text-blue-800"
        >
          Request Clarification →
        </button>
      </div>
    </div>
  );
}
